import { useUserStore } from '@/store/modules/user'
import { getAccessToken, getRefreshToken, removeToken, getTenantId } from '@/services/utils/auth'
import errorCode from '@/services/utils/errorCode'
import { toast, tansParams, showConfirm } from '@/services/utils/common'
import { routerChange } from '@/services/router/index'

/**
 * 请求配置类型
 */
interface RequestOptions extends Partial<UniApp.RequestOptions> {
    url: string
    method: 'GET' | 'POST' | 'PUT' | 'DELETE'
    params?: Record<string, any>
    data?: Record<string, any>
    headers?: Record<string, any>
    baseUrl?: string
    loading?: boolean
}

const timeout = 10000
const baseUrl = import.meta.env.VITE_APP_API_BASEURL + import.meta.env.VITE_APP_PROXY_PREFIX

/**
 * 获取当前页面路径
 */
export const getCurrentPagesUrl = (): string => {
    const pages = getCurrentPages()
    const currentPage = pages[pages.length - 1]
    return currentPage?.route || currentPage?.__route__ || '/'
}

/**
 * 刷新 token
 */
export const refreshToken = async (): Promise<UniApp.RequestSuccessCallbackResult> => {
    const tenantId = getTenantId()

    return (await uni.request({
        method: 'POST',
        url: baseUrl + '/user/app/auth/refresh-token?refreshToken=' + getRefreshToken(),
        header: {
            'tenant-id': tenantId
        }
    })) as unknown as UniApp.RequestSuccessCallbackResult
}

/**
 * 递归处理空字符串
 * @param input
 * @returns
 */
type JsonValue = string | number | boolean | null | JsonObject | JsonArray
interface JsonObject {
    [key: string]: JsonValue
}
interface JsonArray extends Array<JsonValue> {}
function deepReplaceEmptyString<T extends JsonValue>(input: T): T {
    if (input === '') return null as T

    if (Array.isArray(input)) {
        return input.map((item) => deepReplaceEmptyString(item)) as T
    }

    if (typeof input === 'object' && input !== null) {
        const result: any = {}
        for (const key in input) {
            result[key] = deepReplaceEmptyString((input as JsonObject)[key])
        }
        return result
    }

    return input
}

/**
 * 通用请求封装
 */
const request = (config: RequestOptions): Promise<any> => {
    const loading = config?.loading || false
    const isToken = (config.headers || {}).isToken === false
    const tenantId = getTenantId()

    config.header = config.header || {}

    // 添加 token
    if (getAccessToken() && !isToken) {
        config.header['Authorization'] = 'Bearer ' + getAccessToken()
    }

    config.header['tenant-id'] = tenantId

    // 拼接 GET 参数
    if (config.params) {
        let url = config.url + '?' + tansParams(config.params)
        config.url = url.slice(0, -1)
    }

    return new Promise((resolve, reject) => {
        if (loading) uni.showLoading({ title: '加载中...', mask: true })

        uni.request({
            method: config.method || 'GET',
            timeout: config.timeout || timeout,
            url: config.baseUrl || baseUrl + config.url,
            data: deepReplaceEmptyString(config.data || {}),
            header: config.header,
            dataType: 'json',

            success: async (response: UniApp.RequestSuccessCallbackResult) => {
                const resp = response.data as any
                const { code, msg } = resp
                if (code === 500) {
                    toast(msg || '服务器内部错误')
                    return reject(errorCode['500'])
                }else if (code === 401) {
                    toast('登录过期，请重新登录')
                    removeToken()
                    routerChange('redirectTo', 'packageLogin', 'login')
                    return reject(errorCode['401'])
                }else if (code !== 0) {
                    toast(msg)
                    return reject(code)
                }

                resolve(resp)
            },

            fail: (error: any) => {
                let { message } = error
                if (message === 'Network Error') message = '后端接口连接异常'
                else if (message.includes('timeout')) message = '系统接口请求超时'
                else if (message.includes('Request failed with status code')) {
                    message = '系统接口' + message.slice(-3) + '异常'
                }
                toast(message)
                reject(error)
            },

            complete: () => {
                setTimeout(() => uni.hideLoading(), 0)
            }
        })
    })
}

/**
 * GET 请求封装
 */
export const httpGet = (url: string, params?: Record<string, any>): Promise<any> => {
    return request({ url, params, method: 'GET' })
}

/**
 * POST 请求封装
 */
export const httpPost = (url: string, data?: Record<string, any>, params?: Record<string, any>): Promise<any> => {
    return request({ url, data, params, method: 'POST' })
}

request.get = httpGet
request.post = httpPost

export default request
